import ELK from 'elkjs/lib/elk.bundled.js';
import { MarkerType } from '@xyflow/react';
import type { ElkNode } from 'elkjs/lib/elk.bundled.js';
import type { Edge, Node } from '@xyflow/react';

import { EMPTY_ARRAY } from '@e-socna/empty';
import type { ColorEnum, IconEnum } from '@e-socna/data-layer';

import type { RoutePoint } from './points-to-rounded-path.util';

const NODE_WIDTH = 200;
const NODE_HEIGHT = 70;
const START_NODE_WIDTH = 40;
const START_NODE_HEIGHT = 40;
const START_NODE_ID = '__start__';
const EDGE_LABEL_HEIGHT = 16;
const EDGE_LABEL_CHAR_WIDTH = 6.5;
const EDGE_LABEL_PADDING = 12;

const estimateLabelSize = (
  label: string,
): Readonly<{ width: number; height: number }> => ({
  width: label.length * EDGE_LABEL_CHAR_WIDTH + EDGE_LABEL_PADDING,
  height: EDGE_LABEL_HEIGHT,
});

const elk = new ELK();

const LAYOUT_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  'elk.edgeRouting': 'ORTHOGONAL',
  'elk.layered.spacing.nodeNodeBetweenLayers': '90',
  'elk.spacing.nodeNode': '40',
  'elk.spacing.edgeNode': '30',
  'elk.spacing.edgeEdge': '30',
  'elk.spacing.nodeSelfLoop': '50',
  'elk.layered.cycleBreaking.strategy': 'DEPTH_FIRST',
  'elk.edgeLabels.placement': 'CENTER',
  'elk.edgeLabels.inline': 'true',
};

interface WorkflowActionSource {
  readonly _id: string;
  readonly label: string;
  readonly nextNodeId: string;
}

interface WorkflowNodeSource {
  readonly _id: string;
  readonly stateId: string;
  readonly isStartingPoint: boolean;
  readonly isEndPoint: boolean;
  readonly actions: readonly WorkflowActionSource[];
}

export interface WorkflowGraphSource {
  readonly stateMachine: readonly WorkflowNodeSource[];
  readonly initAction: WorkflowActionSource;
}

interface WorkflowState {
  readonly code: string;
  readonly label: string;
  readonly chip: Readonly<{ color: ColorEnum; icon: IconEnum }>;
}

export const lookupStateById = <
  TId extends string,
  TState extends WorkflowState,
>(
  statesById: Record<TId, TState>,
  stateId: string,
): TState | undefined => statesById[stateId as TId];

export type WorkflowStateLookup = (
  stateId: string,
) => WorkflowState | undefined;

interface CompatibleWorkflow {
  readonly label: string;
}

export const lookupWorkflowById = <
  TId extends string,
  TWorkflow extends CompatibleWorkflow,
>(
  workflowsById: Record<TId, TWorkflow>,
  workflowId: string,
): TWorkflow | undefined => workflowsById[workflowId as TId];

export interface WorkflowNodeData extends Record<string, unknown> {
  readonly label: string;
  readonly stateCode?: string;
  readonly color?: ColorEnum;
  readonly icon?: IconEnum;
  readonly isStartMarker?: boolean;
  readonly isEndPoint: boolean;
  readonly raw: unknown;
}

export interface WorkflowEdgeData extends Record<string, unknown> {
  readonly raw: unknown;
  readonly points?: readonly RoutePoint[];
  readonly labelPosition?: RoutePoint;
}

export interface WorkflowSelection {
  readonly kind: 'node' | 'action' | 'workflow';
  readonly raw: unknown;
  readonly title: string;
}

const applyLayout = async (
  nodes: readonly Node<WorkflowNodeData>[],
  edges: readonly Edge<WorkflowEdgeData>[],
): Promise<
  Readonly<{
    nodes: readonly Node<WorkflowNodeData>[];
    edges: readonly Edge<WorkflowEdgeData>[];
  }>
> => {
  const elkGraph: ElkNode = {
    id: 'root',
    layoutOptions: LAYOUT_OPTIONS,
    children: nodes.map((node) => ({
      id: node.id,
      width: node.width,
      height: node.height,
    })),
    edges: edges.map((edge) => {
      const label = typeof edge.label === 'string' ? edge.label : '';

      return {
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target],
        labels: label ? [{ text: label, ...estimateLabelSize(label) }] : [],
      };
    }),
  };

  const layout = await elk.layout(elkGraph);
  const layoutById = new Map(
    layout.children?.map((child) => [child.id, child]),
  );
  const routeById = new Map<string, readonly RoutePoint[]>(
    layout.edges?.map((edge) => {
      const section = edge.sections?.[0];
      const points = section
        ? [
            section.startPoint,
            ...(section.bendPoints ?? EMPTY_ARRAY),
            section.endPoint,
          ]
        : EMPTY_ARRAY;

      return [edge.id, points];
    }),
  );
  const labelPositionById = new Map<string, RoutePoint>(
    layout.edges?.flatMap(
      (edge): readonly (readonly [string, RoutePoint])[] => {
        const placedLabel = edge.labels?.[0];

        if (placedLabel?.x === undefined || placedLabel.y === undefined) {
          return EMPTY_ARRAY;
        }

        return [
          [
            edge.id,
            {
              x: placedLabel.x + (placedLabel.width ?? 0) / 2,
              y: placedLabel.y + (placedLabel.height ?? 0) / 2,
            },
          ],
        ];
      },
    ) ?? EMPTY_ARRAY,
  );

  return {
    nodes: nodes.map((node) => {
      const layoutNode = layoutById.get(node.id);

      return {
        ...node,
        position: { x: layoutNode?.x ?? 0, y: layoutNode?.y ?? 0 },
      };
    }),
    edges: edges.map((edge) => ({
      ...edge,
      type: 'workflowEdge',
      data: {
        ...edge.data,
        points: routeById.get(edge.id),
        labelPosition: labelPositionById.get(edge.id),
      } as WorkflowEdgeData,
    })),
  };
};

export const buildWorkflowGraph = async (
  workflow: Readonly<WorkflowGraphSource>,
  getState: WorkflowStateLookup,
): Promise<
  Readonly<{
    nodes: readonly Node<WorkflowNodeData>[];
    edges: readonly Edge<WorkflowEdgeData>[];
  }>
> => {
  const startingNode = workflow.stateMachine.find(
    (node) => node._id === workflow.initAction.nextNodeId,
  );

  const startMarkerNode: Node<WorkflowNodeData> = {
    id: START_NODE_ID,
    type: 'workflowState',
    data: {
      label: 'Création',
      isStartMarker: true,
      isEndPoint: false,
      raw: workflow.initAction,
    },
    position: { x: 0, y: 0 },
    width: START_NODE_WIDTH,
    height: START_NODE_HEIGHT,
  };

  const stateNodes: readonly Node<WorkflowNodeData>[] =
    workflow.stateMachine.map((node) => {
      const state = getState(node.stateId);

      return {
        id: node._id,
        type: 'workflowState',
        data: {
          label: state?.label ?? node.stateId,
          stateCode: state?.code,
          color: state?.chip.color,
          icon: state?.chip.icon,
          isEndPoint: node.isEndPoint,
          raw: node,
        },
        position: { x: 0, y: 0 },
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      };
    });

  const initEdge: Edge<WorkflowEdgeData> = {
    id: `init-${workflow.initAction._id}`,
    source: START_NODE_ID,
    target: startingNode?._id ?? START_NODE_ID,
    label: workflow.initAction.label,
    animated: true,
    markerEnd: { type: MarkerType.ArrowClosed },
    data: { raw: workflow.initAction },
  };

  const transitionEdges: readonly Edge<WorkflowEdgeData>[] =
    workflow.stateMachine.flatMap((node) =>
      node.actions.map((action) => ({
        id: action._id,
        source: node._id,
        target: action.nextNodeId,
        label: action.label,
        markerEnd: { type: MarkerType.ArrowClosed },
        data: { raw: action },
      })),
    );

  const nodes = [startMarkerNode, ...stateNodes];
  const edges = [initEdge, ...transitionEdges];

  return applyLayout(nodes, edges);
};
